<template>
    <div class="llegada">

        <!-- Cargando -->
        <HyFLoader v-if="loading" variant="inline" />

        <!-- Link inválido -->
        <div v-else-if="error" class="lg-state">
            <h1 class="lg-state__title">{{ $t('arrival.error-title') }}</h1>
            <p>{{ error }}</p>
            <p class="lg-state__hint">{{ $t('arrival.error-hint') }}</p>
        </div>

        <div v-else class="lg-wrapper">

            <!-- ───────────── Formulario ─────────────
                 Las dos partes ven lo mismo: cambia a quién se le comparte. -->
            <template v-if="!alreadyCompleted">

                <div class="lg-head">
                    <p class="lg-eyebrow">{{ $t('arrival.eyebrow') }}</p>
                    <h1 class="lg-title">{{ $t('arrival.contact-title', { other: otraParte }) }}</h1>
                    <p class="lg-sub">{{ $t('arrival.contact-lead', { other: otraParte }) }}</p>
                </div>

                <div class="lg-summary">
                    <div v-if="reservation.accommodationId" class="lg-summary__row">
                        <span class="lg-summary__label">{{ $t('arrival.summary-accommodation') }}</span>
                        <router-link
                            class="lg-summary__link"
                            :to="'/alojamiento/' + reservation.accommodationId"
                            target="_blank"
                        >
                            #{{ reservation.accommodationId }}
                        </router-link>
                    </div>
                    <div v-if="reservation.accommodationAddress" class="lg-summary__row">
                        <span class="lg-summary__label">{{ $t('arrival.summary-address') }}</span>
                        <span class="lg-summary__value">{{ reservation.accommodationAddress }}</span>
                    </div>
                    <div class="lg-summary__row">
                        <span class="lg-summary__label">{{ $t('arrival.summary-checkin') }}</span>
                        <span class="lg-summary__value">{{ reservation.checkIn }}</span>
                    </div>
                    <div v-if="reservation.otherName" class="lg-summary__row">
                        <span class="lg-summary__label">
                            {{ $t(isGuest ? 'arrival.summary-host' : 'arrival.summary-guest') }}
                        </span>
                        <span class="lg-summary__value">{{ reservation.otherName }}</span>
                    </div>
                </div>

                <form class="lg-card" @submit.prevent="submit">

                    <div class="lg-field">
                        <label class="lg-label">{{ $t('arrival.wa-label') }}</label>
                        <div class="lg-phone">
                            <!-- Varios países comparten el mismo código (+1), así que
                                 la key lleva el índice, igual que en el intake. -->
                            <select class="lg-input lg-select" v-model="contacto.codigoPais" :aria-label="$t('arrival.wa-country')">
                                <option
                                    v-for="(pais, index) in paises"
                                    :key="`${pais.code}-${index}`"
                                    :value="pais.value"
                                >
                                    {{ pais.name }}
                                </option>
                            </select>
                            <input
                                class="lg-input"
                                type="tel"
                                inputmode="tel"
                                v-model="contacto.numero"
                                :placeholder="$t('arrival.wa-ph')"
                            />
                        </div>
                        <span v-if="errors.whatsapp" class="lg-error">{{ errors.whatsapp }}</span>
                    </div>

                    <div class="lg-field">
                        <label class="lg-label">{{ $t('arrival.email') }}</label>
                        <input class="lg-input" type="email" v-model="contacto.email" />
                        <span v-if="errors.email" class="lg-error">{{ errors.email }}</span>
                    </div>

                    <div class="lg-field">
                        <label class="lg-label">{{ $t('arrival.message') }}</label>
                        <textarea class="lg-textarea" rows="3" maxlength="300" v-model="contacto.mensaje"></textarea>
                        <p class="lg-hint lg-hint--block lg-hint--right">{{ contacto.mensaje.length }}/300</p>
                        <span v-if="errors.mensaje" class="lg-error">{{ errors.mensaje }}</span>
                    </div>

                    <!-- Vista previa: cómo le va a llegar el mensaje a la otra parte -->
                    <div class="lg-preview" aria-hidden="true">
                        <div class="lg-preview__head">
                            <span class="lg-preview__avatar">{{ inicialPropia }}</span>
                            <div class="lg-preview__who">
                                <span class="lg-preview__name">{{ reservation.myName }}</span>
                                <small class="lg-preview__number">{{ numeroVisible }}</small>
                            </div>
                        </div>
                        <div class="lg-preview__body">
                            <!-- La burbuja respeta los saltos de línea del mensaje:
                                 no puede llevar espacios sueltos alrededor. -->
                            <div class="lg-bubble"><span>{{ contacto.mensaje.trim() || '…' }}</span><time>{{ $t('arrival.preview-now') }}</time></div>
                        </div>
                        <p class="lg-preview__caption">{{ $t('arrival.preview-caption', { other: otraParte }) }}</p>
                    </div>

                    <div class="lg-field">
                        <label class="lg-consent">
                            <input type="checkbox" v-model="contacto.consentimiento" />
                            <span>{{ $t('arrival.consent', { other: otraParte }) }}</span>
                        </label>
                        <span v-if="errors.consentimiento" class="lg-error">{{ errors.consentimiento }}</span>
                    </div>

                    <div v-if="submitError" class="lg-error lg-error--block">{{ submitError }}</div>

                    <div class="lg-actions">
                        <button class="lg-btn" type="submit" :disabled="sending">
                            <span v-if="sending" class="lg-spinner lg-spinner--btn"></span>
                            {{ sending ? $t('arrival.sharing') : $t('arrival.share') }}
                        </button>
                    </div>
                </form>

                <p class="lg-footnote">{{ $t('arrival.contact-footnote') }}</p>
            </template>

            <!-- ───────────── Esperando a la otra parte ───────────── -->
            <div v-else-if="!conectado" class="lg-card lg-closed" aria-live="polite">
                <div class="lg-closed__icon">
                    <svg width="26" height="26" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#1D9E75" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h2 class="lg-closed__title">{{ $t('arrival.waiting-title') }}</h2>
                <p class="lg-closed__text">{{ $t('arrival.waiting-text', { other: otraParte }) }}</p>
            </div>

            <!-- ───────────── Las dos partes compartieron ───────────── -->
            <div v-else class="lg-card lg-closed" aria-live="polite">
                <div class="lg-closed__icon lg-closed__icon--wa">🤝</div>
                <h2 class="lg-closed__title">{{ $t('arrival.connected-title') }}</h2>
                <p class="lg-closed__text">{{ $t('arrival.connected-text', { other: otraParte }) }}</p>
                <a
                    v-if="linkWhatsapp"
                    class="lg-wa-btn"
                    :href="linkWhatsapp"
                    target="_blank"
                    rel="noopener"
                >
                    {{ $t('arrival.wa-button') }}
                </a>
            </div>

        </div>

        <!-- Pop up de agradecimiento. Sale al guardar y, unos segundos después,
             seguimos al perfil (o al inicio, si entró desde el correo sin
             sesión iniciada). -->
        <div v-if="success" class="lg-modal">
            <div class="lg-modal__backdrop" @click="salir"></div>
            <div class="lg-modal__content">
                <div class="lg-modal__icon">
                    <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                        <path d="M5 12.5l4.5 4.5L19 7.5" stroke="#1D9E75" stroke-width="2.2" stroke-linecap="round" stroke-linejoin="round"/>
                    </svg>
                </div>
                <h2 class="lg-modal__title">{{ $t('arrival.thanks-title') }}</h2>
                <p class="lg-modal__text">
                    {{ $t(conectado ? 'arrival.thanks-text-connected' : 'arrival.thanks-text', { other: otraParte }) }}
                </p>

                <a
                    v-if="conectado && linkWhatsapp"
                    class="lg-wa-btn lg-wa-btn--modal"
                    :href="linkWhatsapp"
                    target="_blank"
                    rel="noopener"
                >
                    {{ $t('arrival.wa-button') }}
                </a>

                <button class="lg-btn lg-btn--modal" @click="salir">
                    {{ $t(estaLogueado ? 'arrival.success-profile' : 'arrival.success-home') }}
                </button>
                <p class="lg-modal__hint">{{ $t('arrival.success-redirect') }}</p>
            </div>
        </div>

    </div>
</template>

<script>
import HyFLoader from '@/components/Loader/HyFLoader.vue';
import axios from 'axios';
import countriesData from '@/assets/data/users-countries-data.json';

/**
 * Formulario de contacto de una reserva pagada.
 *
 * Es una sola landing para las dos partes: el token del link dice si el que
 * entra es el huésped o el anfitrión, y de eso solo dependen los textos. Cada
 * uno deja su WhatsApp, su email y un mensaje para el otro. No pide login: el
 * link viaja en el correo del pago confirmado.
 *
 * Cuando los dos completan su mitad, la API les manda a los dos el contacto del
 * otro por correo y acá se muestra el botón para escribirle por WhatsApp.
 */

/**
 * Códigos de país del selector de WhatsApp: el mismo listado que usa el
 * formulario de intake (assets/data/users-countries-data.json).
 *
 * De más largo a más corto, para separar un número ya guardado sin confundir
 * +1684 con +1.
 */
const CODIGOS = countriesData
    .map((pais) => pais.value)
    .sort((a, b) => b.length - a.length);

export default {
    components: { HyFLoader },
    name: 'LlegadaContacto',

    props: {
        // Respuesta del endpoint que ya trajo `Llegada.vue`. Si viene, no se
        // vuelve a consultar; si no, el componente se arregla solo.
        precargado: {
            type: Object,
            default: null,
        },
    },

    data() {
        return {
            loading: true,
            sending: false,
            error: null,
            submitError: null,
            success: false,
            redirectTimer: null,

            audience: null,
            alreadyCompleted: false,
            reservation: {},
            // Contacto de la otra parte. Solo llega cuando los dos completaron.
            other: null,

            contacto: {
                codigoPais: '56',
                numero: '',
                email: '',
                mensaje: '',
                consentimiento: false,
            },

            errors: {},
        };
    },

    computed: {
        paises() {
            return countriesData;
        },

        isGuest() {
            return this.audience === 'guest';
        },

        /** "anfitrión" o "huésped", según a quién se le comparten los datos. */
        otraParte() {
            return this.$t(this.isGuest ? 'arrival.other-host' : 'arrival.other-guest');
        },

        /** Las dos partes ya compartieron: hay contacto para mostrar. */
        conectado() {
            return !!(this.other && this.other.whatsapp);
        },

        linkWhatsapp() {
            if (!this.conectado) {
                return null;
            }

            const numero = this.soloDigitos(this.other.whatsapp);
            const saludo = this.$t('arrival.wa-greeting', {
                name: this.reservation.myName || '',
                number: this.reservation.number || '',
            });

            return `https://wa.me/${numero}?text=${encodeURIComponent(saludo)}`;
        },

        /** Inicial propia para el avatar de la vista previa. */
        inicialPropia() {
            return (this.reservation.myName || 'A').charAt(0).toUpperCase();
        },

        /** El número tal como se ve en la vista previa: +56 912345678. */
        numeroVisible() {
            const numero = this.soloDigitos(this.contacto.numero);

            return `+${this.contacto.codigoPais} ${numero || '…'}`;
        },

        estaLogueado() {
            return this.$store.getters.isLoggedIn;
        },

        idreserva() {
            return this.$route.params.idreserva;
        },
        token() {
            return this.$route.params.token;
        },
    },

    created() {
        this.cargar();
    },

    methods: {
        cargar() {
            // `Llegada.vue` ya consultó el endpoint para saber qué formulario
            // mostrar, así que reaprovecha esa respuesta en vez de pedirla otra vez.
            if (this.precargado) {
                this.aplicar(this.precargado);
                this.loading = false;
                return;
            }

            axios
                .get(`${process.env.VUE_APP_API_URL}arrival/${this.idreserva}/${this.token}`)
                .then(({ data }) => {
                    this.aplicar(data);
                })
                .catch(() => {
                    this.error = this.$t('arrival.error-invalid');
                })
                .finally(() => {
                    this.loading = false;
                });
        },

        aplicar(data) {
            this.audience = data.audience;
            this.reservation = data.reservation || {};
            this.alreadyCompleted = !!data.completed;
            this.other = data.other || null;

            this.precargar(data.data);
        },

        /**
         * El WhatsApp y el email vienen del perfil, así que el formulario abre
         * con los dos campos ya puestos y un mensaje sugerido: solo hay que
         * revisarlos y confirmar.
         */
        precargar(datos) {
            const info = datos || {};

            if (info.whatsapp) {
                this.separarWhatsapp(info.whatsapp);
            }

            if (info.email) {
                this.contacto.email = info.email;
            }

            this.contacto.mensaje = info.mensaje || this.mensajeSugerido();
        },

        mensajeSugerido() {
            const nombre = this.reservation.myName
                ? String(this.reservation.myName).split(' ')[0]
                : null;

            if (!nombre) {
                return this.$t('arrival.message-default-anon', { other: this.otraParte });
            }

            if (this.isGuest) {
                return this.$t('arrival.message-default-guest', {
                    name: nombre,
                    date: this.reservation.checkIn,
                });
            }

            return this.$t('arrival.message-default-host', { name: nombre });
        },

        /**
         * Separa un número guardado en código de país + número.
         *
         * Con el "+" adelante se confía en el prefijo tal cual. Sin el "+" (los
         * teléfonos del perfil suelen estar así, "541121783961") solo se separan
         * los códigos de dos o tres dígitos y siempre que queden al menos 8: un
         * número local que empiece con 1 no es de Estados Unidos.
         */
        separarWhatsapp(valor) {
            const digitos = this.soloDigitos(valor);

            if (!digitos) {
                return;
            }

            const internacional = String(valor).trim().charAt(0) === '+';

            const codigo = CODIGOS
                .filter((c) => internacional || c.length > 1)
                .find((c) => digitos.indexOf(c) === 0
                    && digitos.length - c.length >= (internacional ? 1 : 8));

            if (codigo) {
                this.contacto.codigoPais = codigo;
                this.contacto.numero = digitos.slice(codigo.length);
            } else {
                this.contacto.numero = digitos;
            }
        },

        soloDigitos(valor) {
            return String(valor || '').replace(/\D/g, '');
        },

        validar() {
            this.errors = {};

            const numero = this.soloDigitos(this.contacto.numero);

            if (numero.length < 6 || numero.length > 14) {
                this.$set(this.errors, 'whatsapp', this.$t('arrival.err-wa'));
            }
            if (!/^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/.test(String(this.contacto.email || '').trim())) {
                this.$set(this.errors, 'email', this.$t('arrival.err-email'));
            }
            if (String(this.contacto.mensaje || '').trim().length < 2) {
                this.$set(this.errors, 'mensaje', this.$t('arrival.err-message'));
            }
            if (!this.contacto.consentimiento) {
                this.$set(this.errors, 'consentimiento', this.$t('arrival.err-consent'));
            }

            return Object.keys(this.errors).length === 0;
        },

        submit() {
            this.submitError = null;

            if (!this.validar()) {
                return;
            }

            this.sending = true;

            const base = `${process.env.VUE_APP_API_URL}arrival/${this.idreserva}/${this.token}`;
            const url = this.isGuest ? `${base}/guest` : `${base}/host`;
            const payload = {
                whatsapp: `+${this.contacto.codigoPais}${this.soloDigitos(this.contacto.numero)}`,
                email: String(this.contacto.email).trim(),
                mensaje: String(this.contacto.mensaje).trim(),
                consentimiento: true,
            };

            axios
                .post(url, payload)
                .then(({ data }) => {
                    this.alreadyCompleted = true;
                    // Si la otra parte ya había completado, la respuesta trae su
                    // contacto y se pasa directo a la pantalla de conectados.
                    this.other = data.other || null;
                    window.scrollTo({ top: 0 });

                    this.success = true;
                    this.redirectTimer = setTimeout(this.salir, 6000);
                })
                .catch((error) => {
                    const status = error.response && error.response.status;

                    if (status === 409) {
                        this.alreadyCompleted = true;
                        this.submitError = null;
                    } else if (status === 422) {
                        this.submitError = this.$t('arrival.err-submit-422');
                    } else {
                        this.submitError = this.$t('arrival.err-submit');
                    }
                })
                .finally(() => {
                    this.sending = false;
                });
        },

        /**
         * Después de guardar seguimos al perfil. Si el link se abrió desde el
         * correo sin sesión iniciada, va al inicio: el formulario nunca pide
         * login, así que no tiene sentido mandarlo a iniciar sesión.
         */
        salir() {
            if (this.redirectTimer) {
                clearTimeout(this.redirectTimer);
                this.redirectTimer = null;
            }

            const destino = this.estaLogueado
                ? { name: 'editar-perfil' }
                : { name: 'home' };

            this.$router.push(destino).catch(() => {});
        },
    },

    beforeDestroy() {
        if (this.redirectTimer) {
            clearTimeout(this.redirectTimer);
        }
    },
};
</script>

<style scoped lang="scss">
.llegada {
    background: #fbfaf8;
    min-height: 70vh;
    padding: 2.5rem 1rem 3.5rem;
    /* DefaultLayout reserva la altura del header en .main-content, pero el header
       es sticky y ya ocupa su lugar en el flujo: ese padding dejaba una franja
       blanca sobre el formulario. */
    margin-top: -$mobile-header-height;
    /* Nada puede empujar la página a lo ancho: con el zoom alto o en un celular
       angosto, una dirección larga haría aparecer scroll horizontal. */
    overflow-x: hidden;

    @media (min-width: $md) {
        margin-top: -$desktop-header-height;
    }
}

.lg-wrapper {
    max-width: 640px;
    margin: 0 auto;
}

// ── Estados ────────────────────────────────────────────────────────────────

.lg-state {
    max-width: 480px;
    margin: 0 auto;
    text-align: center;
    color: #5f5e5a;
    font-size: 14px;

    &__title {
        font-size: 20px;
        font-weight: 600;
        color: #085041;
        margin-bottom: 10px;
    }

    &__hint {
        font-size: 13px;
        color: #888780;
        margin-top: 10px;
    }
}

// ── Encabezado ─────────────────────────────────────────────────────────────

.lg-eyebrow {
    font-size: 11px;
    font-weight: 500;
    letter-spacing: 0.08em;
    text-transform: uppercase;
    color: #ba7517;
    margin-bottom: 8px;
}

.lg-title {
    font-size: 24px;
    font-weight: 600;
    color: #1a1a18;
    margin: 0 0 6px 0;
}

.lg-sub {
    font-size: 14px;
    color: #5f5e5a;
    margin: 0 0 1.5rem 0;
}

// ── Resumen ────────────────────────────────────────────────────────────────

.lg-summary {
    background: #fff7f1;
    border: 1px solid #f3ddcb;
    border-radius: 12px;
    padding: 14px 18px;
    margin-bottom: 1.25rem;

    &__row {
        display: flex;
        justify-content: space-between;
        gap: 14px;
        padding: 5px 0;
        font-size: 13px;
    }

    &__label {
        color: #8a6a52;
        flex-shrink: 0;
    }

    /* `min-width: 0` para que una dirección larga se corte dentro de la fila en
       vez de estirarla. */
    &__value {
        color: #1a1a18;
        font-weight: 500;
        text-align: right;
        min-width: 0;
        overflow-wrap: anywhere;
    }

    &__link {
        color: #ed6b16;
        font-weight: 600;
        text-decoration: none;
        text-align: right;
        flex-shrink: 0;

        &:hover {
            text-decoration: underline;
        }
    }
}

// ── Formulario ya completado ───────────────────────────────────────────────

.lg-closed {
    text-align: center;

    &__icon {
        width: 52px;
        height: 52px;
        border-radius: 50%;
        background: #eaf7f1;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 14px;

        &--wa {
            background: #fff4ec;
            font-size: 24px;
        }
    }

    &__title {
        font-size: 18px;
        font-weight: 600;
        color: #1a1a18;
        margin: 0 0 8px 0;
    }

    &__text {
        font-size: 14px;
        color: #5f5e5a;
        line-height: 1.55;
        margin: 0;
    }
}

.lg-wa-btn {
    display: inline-flex;
    align-items: center;
    gap: 8px;
    margin-top: 20px;
    padding: 13px 24px;
    background: #25d366;
    color: #fff;
    text-decoration: none;
    font-weight: 700;
    font-size: 15px;
    border-radius: 999px;

    &:hover {
        opacity: 0.9;
    }

    &--modal {
        margin-top: 4px;
    }
}

// ── Formulario ─────────────────────────────────────────────────────────────

.lg-card {
    background: #fff;
    border: 1px solid #e6e4df;
    border-radius: 14px;
    padding: 1.75rem 1.5rem;
}

.lg-field {
    margin-bottom: 1.25rem;
}

.lg-label {
    display: block;
    font-size: 14px;
    font-weight: 500;
    color: #1a1a18;
    margin-bottom: 8px;
}

.lg-hint {
    font-size: 12px;
    color: #888780;
    font-weight: 400;

    &--block {
        display: block;
        margin: 6px 0 0 0;
    }

    &--right {
        text-align: right;
    }
}

.lg-input,
.lg-textarea {
    width: 100%;
    border: 1px solid #d6d5d0;
    border-radius: 8px;
    padding: 0 12px;
    height: 42px;
    font-size: 14px;
    color: #1a1a18;
    background: #fff;
    outline: none;
    transition: border-color 0.12s;

    &:focus {
        border-color: #ed6b16;
    }
}

.lg-textarea {
    height: auto;
    padding: 10px 12px;
    resize: vertical;
    line-height: 1.5;
}

// ── Contacto ───────────────────────────────────────────────────────────────

/* Dos columnas iguales: el selector lleva el nombre completo del país
   ("Argentina (+54)") y no entra en una columna angosta. */
.lg-phone {
    display: grid;
    grid-template-columns: minmax(0, 1fr) minmax(0, 1fr);
    gap: 8px;
}

.lg-select {
    padding: 0 8px;
    background: #fff;
    text-overflow: ellipsis;
}

/* Vista previa de cómo le llega el mensaje a la otra parte por WhatsApp. */
.lg-preview {
    margin: 0 0 1.25rem 0;
    border: 1px solid #e6e4df;
    border-radius: 12px;
    overflow: hidden;

    &__head {
        display: flex;
        align-items: center;
        gap: 10px;
        background: #075e54;
        color: #fff;
        padding: 10px 14px;
        font-size: 13px;
    }

    &__avatar {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: rgba(255, 255, 255, 0.25);
        display: flex;
        align-items: center;
        justify-content: center;
        font-size: 13px;
        flex-shrink: 0;
    }

    /* El nombre y el número se recortan con puntos suspensivos en vez de
       desbordar la cabecera en pantallas angostas. */
    &__who {
        min-width: 0;
    }

    &__name {
        display: block;
        font-weight: 600;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__number {
        display: block;
        font-size: 11px;
        opacity: 0.8;
        overflow: hidden;
        text-overflow: ellipsis;
        white-space: nowrap;
    }

    &__body {
        background: #ece5dd;
        padding: 16px 14px 18px;
        min-height: 80px;
    }

    &__caption {
        margin: 0;
        padding: 8px 14px;
        font-size: 12px;
        color: #888780;
        background: #fff;
        border-top: 1px solid #e6e4df;
    }
}

.lg-bubble {
    max-width: 88%;
    margin-left: auto;
    background: #dcf8c6;
    border-radius: 10px 2px 10px 10px;
    padding: 8px 10px 18px;
    font-size: 14px;
    line-height: 1.4;
    color: #1a1a18;
    position: relative;
    white-space: pre-wrap;
    word-wrap: break-word;
    box-shadow: 0 1px 1px rgba(0, 0, 0, 0.08);

    time {
        position: absolute;
        right: 8px;
        bottom: 4px;
        font-size: 10px;
        color: #667781;
    }
}

.lg-consent {
    display: flex;
    align-items: flex-start;
    gap: 10px;
    margin: 0;
    padding: 12px 14px;
    background: #fbfaf8;
    border: 1px solid #e6e4df;
    border-radius: 10px;
    font-size: 13px;
    line-height: 1.45;
    color: #1a1a18;
    font-weight: 400;
    cursor: pointer;

    input {
        width: 18px;
        height: 18px;
        margin: 1px 0 0 0;
        flex: none;
        accent-color: #ed6b16;
    }
}

.lg-footnote {
    font-size: 12px;
    color: #888780;
    text-align: center;
    line-height: 1.5;
    margin: 1.1rem 0 0 0;
}

.lg-error {
    display: block;
    font-size: 12px;
    color: #d85a30;
    margin-top: 6px;

    &--block {
        margin: 0 0 1rem 0;
        padding: 10px 14px;
        background: #fff4f0;
        border: 1px solid #d85a30;
        border-radius: 8px;
    }
}

.lg-actions {
    display: flex;
    justify-content: flex-end;
    margin-top: 1.5rem;
}

.lg-btn {
    display: inline-flex;
    align-items: center;
    justify-content: center;
    gap: 8px;
    height: 44px;
    padding: 0 28px;
    border: none;
    border-radius: 999px;
    background: #ed6b16;
    color: #fff;
    font-size: 15px;
    font-weight: 600;
    cursor: pointer;
    transition: opacity 0.15s;

    &:hover:not(:disabled) {
        opacity: 0.9;
    }

    &:disabled {
        opacity: 0.55;
        cursor: not-allowed;
    }

    &--modal {
        width: 100%;
        margin-top: 18px;
    }
}

// ── Pop up de agradecimiento ───────────────────────────────────────────────

.lg-modal {
    position: fixed;
    inset: 0;
    z-index: 10000;
    display: flex;
    align-items: center;
    justify-content: center;
    padding: 1rem;

    &__backdrop {
        position: absolute;
        inset: 0;
        background: rgba(0, 0, 0, 0.55);
    }

    /* Con el zoom alto o en un celular apaisado el pop up puede no entrar de
       alto: se hace scrolleable en vez de quedar cortado. */
    &__content {
        position: relative;
        z-index: 2;
        background: #fff;
        border-radius: 16px;
        padding: 2rem 1.75rem 1.75rem;
        max-width: 420px;
        width: 100%;
        max-height: 90vh;
        overflow-y: auto;
        text-align: center;
        box-shadow: 0 18px 40px rgba(0, 0, 0, 0.18);
    }

    &__icon {
        width: 56px;
        height: 56px;
        border-radius: 50%;
        background: #eaf7f1;
        display: flex;
        align-items: center;
        justify-content: center;
        margin: 0 auto 14px;
    }

    &__title {
        font-size: 20px;
        font-weight: 600;
        color: #1a1a18;
        margin: 0 0 8px 0;
    }

    &__text {
        font-size: 14px;
        color: #5f5e5a;
        line-height: 1.55;
        margin: 0;
    }

    &__hint {
        font-size: 12px;
        color: #888780;
        margin: 12px 0 0 0;
    }
}

// ── Spinner ────────────────────────────────────────────────────────────────

.lg-spinner {
    display: inline-block;
    width: 18px;
    height: 18px;
    border: 2px solid rgba(237, 107, 22, 0.25);
    border-top-color: #ed6b16;
    border-radius: 50%;
    animation: lg-spin 0.7s linear infinite;
    margin: 0 auto 10px;

    &--btn {
        width: 14px;
        height: 14px;
        border-color: rgba(255, 255, 255, 0.45);
        border-top-color: #fff;
        margin: 0;
    }
}

@keyframes lg-spin {
    to { transform: rotate(360deg); }
}

// ── Pantallas chicas y zoom ────────────────────────────────────────────────
//
// Los breakpoints van por ancho de viewport, así que también entran cuando se
// hace zoom en el navegador: al 200% un escritorio de 1280 px se comporta como
// una tablet de 640, y al 400% como un celular.

/* Tablet y desktop con zoom medio: el contenido ya está limitado a 640 px, solo
   se achica el aire de los costados. */
@media (max-width: 700px) {
    .llegada { padding: 2rem 0.875rem 3rem; }
}

/* El resumen deja de ser etiqueta-a-la-izquierda / valor-a-la-derecha: con poco
   ancho una dirección larga quedaba apretada contra su etiqueta. */
@media (max-width: 620px) {
    .lg-summary__row {
        flex-direction: column;
        gap: 2px;
    }

    .lg-summary__value,
    .lg-summary__link {
        text-align: left;
    }
}

@media (max-width: 520px) {
    .lg-title { font-size: 21px; }
    .lg-card { padding: 1.25rem 1rem; }

    /* Safari en iPhone hace zoom solo al enfocar un campo de menos de 16px y
       deja la página corrida: con 16px no se mueve. */
    .lg-input,
    .lg-textarea { font-size: 16px; }

    .lg-phone { grid-template-columns: 1fr; gap: 8px; }
    .lg-btn { width: 100%; }
    .lg-actions { justify-content: stretch; }
    .lg-wa-btn { width: 100%; justify-content: center; }
    .lg-modal__content { padding: 1.5rem 1.25rem 1.25rem; }
}

/* Celulares angostos (y zoom muy alto): se recorta el aire para que el
   formulario siga entrando sin scroll lateral. */
@media (max-width: 380px) {
    .llegada { padding: 1.5rem 0.75rem 2.5rem; }
    .lg-title { font-size: 19px; }
    .lg-summary { padding: 12px 14px; }
    .lg-card { padding: 1rem 0.875rem; }
    .lg-consent { padding: 10px 12px; }
    .lg-bubble { max-width: 100%; }
}
</style>
